import { useState, useEffect } from 'react'
import { Table, Card, Button, Space, Modal, Form, Input, Select, DatePicker, message, Tag, Popconfirm, Steps } from 'antd'
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons'
import { deliveryApi } from '../services/api'
import PrintDeliverySheet from '../components/PrintDeliverySheet'

const { Option } = Select
const { TextArea } = Input

interface OrderItem {
  id: number
  product_name: string
  product_code: string
  quantity: string
  unit: string
  unit_price: string
  total_price: string
}

interface DeliveryTask {
  id: number
  task_no: string
  order_id: number
  order_no: string
  driver_name: string
  driver_phone: string
  address: string
  contact_name: string
  contact_phone: string
  status: number
  delivery_time: string
  completed_time: string
  remark: string
  created_at: string
  order?: {
    order_no: string
    total_amount?: string
    final_amount?: string
    items?: OrderItem[]
    deliveryAddress?: {
      address: string
      contact_name: string
      contact_phone: string
    }
  }
}

const statusMap: Record<number, { text: string; color: string }> = {
  0: { text: '待分配', color: 'default' },
  1: { text: '已分配', color: 'blue' },
  2: { text: '配送中', color: 'orange' },
  3: { text: '已完成', color: 'green' },
  4: { text: '已取消', color: 'red' }
}

export default function Delivery() {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<DeliveryTask[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [detailVisible, setDetailVisible] = useState(false)
  const [assignVisible, setAssignVisible] = useState(false)
  const [printVisible, setPrintVisible] = useState(false)
  const [currentTask, setCurrentTask] = useState<DeliveryTask | null>(null)
  const [assignForm] = Form.useForm()
  const [searchForm] = Form.useForm()

  const handlePrint = (task: DeliveryTask | null) => {
    if (!task) return
    setCurrentTask(task)
    setPrintVisible(true)
  }

  useEffect(() => {
    loadData()
  }, [page, pageSize])

  const loadData = async () => {
    setLoading(true)
    try {
      const values = searchForm.getFieldsValue()
      const res = await deliveryApi.getList({
        page,
        page_size: pageSize,
        ...values
      })
      if (res.code === 200) {
        setData(res.data?.list || [])
        setTotal(res.data?.total || 0)
      }
    } catch (err) {
      message.error('加载配送任务失败')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    setPage(1)
    loadData()
  }

  const handleReset = () => {
    searchForm.resetFields()
    setPage(1)
    loadData()
  }

  const handleDetail = async (record: DeliveryTask) => {
    try {
      // 先获取详情（包含商品清单等完整数据）
      const res = await deliveryApi.get(record.id)
      if (res.code === 200) {
        setCurrentTask(res.data)
      } else {
        setCurrentTask(record)
      }
    } catch (e) {
      setCurrentTask(record)
    }
    setDetailVisible(true)
  }

  const handleAssign = (record: DeliveryTask) => {
    setCurrentTask(record)
    assignForm.resetFields()
    setAssignVisible(true)
  }

  const handleAssignSubmit = async () => {
    try {
      const values = await assignForm.validateFields()
      const res = await deliveryApi.assign(currentTask!.id, values)
      if (res.code === 200) {
        message.success('分配成功')
        setAssignVisible(false)
        loadData()
      } else {
        message.error(res.message || '分配失败')
      }
    } catch (err) {
      message.error('分配失败')
    }
  }

  const handleStartDelivery = async (id: number) => {
    try {
      const res = await deliveryApi.startDelivery(id)
      if (res.code === 200) {
        message.success('已开始配送')
        loadData()
      } else {
        message.error(res.message || '操作失败')
      }
    } catch (err) {
      message.error('操作失败')
    }
  }

  const handleComplete = async (id: number) => {
    try {
      const res = await deliveryApi.complete(id)
      if (res.code === 200) {
        message.success('配送完成')
        loadData()
      } else {
        message.error(res.message || '操作失败')
      }
    } catch (err) {
      message.error('操作失败')
    }
  }

  const handleCancel = async (id: number) => {
    try {
      const res = await deliveryApi.cancel(id)
      if (res.code === 200) {
        message.success('已取消配送')
        loadData()
      } else {
        message.error(res.message || '取消失败')
      }
    } catch (err) {
      message.error('取消失败')
    }
  }

  const columns = [
    { title: '任务ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: '订单号', dataIndex: 'order_no', key: 'order_no', width: 150 },
    { title: '配送员', dataIndex: 'driver_name', key: 'driver_name', width: 100 },
    { title: '配送员电话', dataIndex: 'driver_phone', key: 'driver_phone', width: 120 },
    { title: '收货地址', dataIndex: 'address', key: 'address', width: 200, ellipsis: true },
    { title: '收货人', dataIndex: 'contact_name', key: 'contact_name', width: 100 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v: number) => <Tag color={statusMap[v]?.color}>{statusMap[v]?.text}</Tag>
    },
    { title: '配送时间', dataIndex: 'delivery_time', key: 'delivery_time', width: 160, render: (v: string) => v?.substring(0, 16).replace('T', ' ') },
    {
      title: '操作',
      key: 'action',
      width: 250,
      render: (_: any, record: DeliveryTask) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleDetail(record)}>详情</Button>
          {record.status === 0 && (
            <Button type="link" size="small" onClick={() => handleAssign(record)}>分配</Button>
          )}
          {record.status === 1 && (
            <Button type="link" size="small" onClick={() => handleStartDelivery(record.id)}>开始配送</Button>
          )}
          {record.status === 2 && (
            <Button type="link" size="small" onClick={() => handleComplete(record.id)}>完成</Button>
          )}
          {(record.status === 0 || record.status === 1) && (
            <Popconfirm title="确认取消该配送任务?" onConfirm={() => handleCancel(record.id)}>
              <Button type="link" size="small" danger>取消</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ]

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Form form={searchForm} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="order_no">
            <Input placeholder="订单号" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="driver_name">
            <Input placeholder="配送员" style={{ width: 120 }} />
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="状态" style={{ width: 120 }} allowClear>
              {Object.entries(statusMap).map(([k, v]) => <Option key={k} value={Number(k)}>{v.text}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) }
          }}
        />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="配送任务详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="print" type="primary" onClick={() => handlePrint(currentTask)}>
            打印配送单
          </Button>
        ]}
        width={700}
      >
        {currentTask && (
          <div>
            <Steps
              current={currentTask.status}
              size="small"
              items={[
                { title: '待分配' },
                { title: '已分配' },
                { title: '配送中' },
                { title: '已完成' }
              ]}
              style={{ marginBottom: 24 }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <p><strong>任务ID：</strong>{currentTask.id}</p>
              <p><strong>订单号：</strong>{currentTask.order?.order_no || '-'}</p>
              <p><strong>配送员：</strong>{currentTask.driver_name || '-'}</p>
              <p><strong>配送员电话：</strong>{currentTask.driver_phone || '-'}</p>
              <p><strong>收货人：</strong>{currentTask.order?.deliveryAddress?.contact_name || '-'}</p>
              <p><strong>联系电话：</strong>{currentTask.order?.deliveryAddress?.contact_phone || '-'}</p>
              <p style={{ gridColumn: '1 / -1' }}><strong>收货地址：</strong>{currentTask.order?.deliveryAddress?.address || '-'}</p>
              <p><strong>配送时间：</strong>{currentTask.delivery_time?.substring(0, 16).replace('T', ' ') || '-'}</p>
              <p><strong>完成时间：</strong>{currentTask.completed_time?.substring(0, 16).replace('T', ' ') || '-'}</p>
              <p><strong>状态：</strong><Tag color={statusMap[currentTask.status]?.color}>{statusMap[currentTask.status]?.text}</Tag></p>
              <p><strong>订单金额：</strong>¥{currentTask.order?.final_amount || '-'}</p>
            </div>
            
            {/* 商品清单 */}
            {currentTask.order?.items && currentTask.order.items.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: 8 }}>商品清单</h4>
                <table style={{ width: '100%', fontSize: 14 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #eee' }}>
                      <th style={{ textAlign: 'left', padding: '8px 0' }}>商品名称</th>
                      <th style={{ textAlign: 'center', padding: '8px 0' }}>数量</th>
                      <th style={{ textAlign: 'right', padding: '8px 0' }}>单价</th>
                      <th style={{ textAlign: 'right', padding: '8px 0' }}>小计</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentTask.order.items.map((item: OrderItem) => (
                      <tr key={item.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '6px 0' }}>{item.product_name}</td>
                        <td style={{ textAlign: 'center', padding: '6px 0' }}>{item.quantity} {item.unit}</td>
                        <td style={{ textAlign: 'right', padding: '6px 0' }}>¥{item.unit_price}</td>
                        <td style={{ textAlign: 'right', padding: '6px 0' }}>¥{item.total_price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {currentTask.remark && (
              <p style={{ marginTop: 16 }}><strong>备注：</strong>{currentTask.remark}</p>
            )}
          </div>
        )}
      </Modal>
      
      {/* 打印配送单 */}
      {currentTask && (
        <PrintDeliverySheet
          task={currentTask}
          visible={printVisible}
          onClose={() => setPrintVisible(false)}
        />
      )}

      {/* 分配弹窗 */}
      <Modal
        title="分配配送员"
        open={assignVisible}
        onOk={handleAssignSubmit}
        onCancel={() => setAssignVisible(false)}
      >
        <Form form={assignForm} labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
          <Form.Item name="driver_name" label="配送员姓名" rules={[{ required: true, message: '请输入配送员姓名' }]}>
            <Input placeholder="请输入配送员姓名" />
          </Form.Item>
          <Form.Item name="driver_phone" label="配送员电话" rules={[{ required: true, message: '请输入配送员电话' }]}>
            <Input placeholder="请输入配送员电话" />
          </Form.Item>
          <Form.Item name="delivery_time" label="预计配送时间">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
